import React, { FC } from "react";
import AdminLayout from "../../layouts/admin/AdminLayout";
import { useEpgCoverage, useVodContentReport } from "../../services/epg/Query";
import CoverageReport from "./epg/components/report/CoverageReport";
import ContentStats from "./epg/components/report/ContentStats";
import ImageStats from "./epg/components/report/ImageStats";

const AdminHomeView: FC = () => {
    const epgCoverage = useEpgCoverage();
    const vodContentReport = useVodContentReport();

    const handleExport = () => {
        if(!epgCoverage.data)
            return;

        if(!vodContentReport.data)
            return;

        const content = `EPG Coverage

id, latestEndDateTime
${epgCoverage.data.channelCoverages
    .map(
        (channelCoverage) =>
            `"${channelCoverage.id}", ${new Date(channelCoverage.latestEndDateTime).toString()}"`,
    )
    .join("\n")}

EPG Stats:
 - ${new Intl.NumberFormat("en-US")
            .format(epgCoverage.data.summary.uniqueEpgEventCount)
        } Distinct Events in Current EPG Data Set.
 - ${new Intl.NumberFormat("en-US").format(
     epgCoverage.data.summary.totalEpgEventAiringsCount)
        } EPG Event Airings (Average of ${
            new Intl.NumberFormat("en-US")
                .format(epgCoverage.data.summary.totalEpgEventAiringsCount / 10)
        } per channel).
 - ${new Intl.NumberFormat("en-US")
            .format((epgCoverage.data.summary).imagesEpgEventCount)
        } images automatically transformed and uploaded in Current EPG Data Set.

VOD Stats
 - ${new Intl.NumberFormat("en-US").format(vodContentReport.data.series)} Series and ${
            new Intl.NumberFormat("en-US").format(vodContentReport.data.seasons)} Seasons
 - ${new Intl.NumberFormat("en-US").format(vodContentReport.data.episodes)} Episodes
 - ${new Intl.NumberFormat("en-US").format(vodContentReport.data.standaloneItems)} Standalone Items
 - ${new Intl.NumberFormat("en-US")
            .format(vodContentReport.data.imagesTotal)} images automatically transformed and uploaded.
   - ${new Intl.NumberFormat("en-US")
            .format(vodContentReport.data.contentImagesReport.series)} series images automatically transformed and uploaded.
   - ${new Intl.NumberFormat("en-US")
            .format(vodContentReport.data.contentImagesReport.season)} season images automatically transformed and uploaded.
   - ${new Intl.NumberFormat("en-US")
            .format(vodContentReport.data.contentImagesReport.episode)} episode images automatically transformed and uploaded.
   - ${new Intl.NumberFormat("en-US")
            .format(vodContentReport.data.contentImagesReport.standalone)} standalone images automatically transformed and uploaded.
   - ${new Intl.NumberFormat("en-US")
            .format(vodContentReport.data.contentImagesReport.library)} library images automatically transformed and uploaded.
- ${new Intl.NumberFormat("en-US")
            .format(Math.round(vodContentReport.data.totalRunTime / 60 / 60))} hours
`;

        const blob = new Blob([ content ], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "report.txt";
        link.click();

        URL.revokeObjectURL(url);
    };

    return (
        <>
            <AdminLayout heading="Home">
                <div>
                    <div className="h-full flex flex-col mr-6 px-4 gap-4 mb-20 bg-[ffffff]">
                        <div className={`w-full flex justify-end`}>
                            {vodContentReport.data && epgCoverage.data &&
                                <button
                                onClick = {handleExport}
                                className = {`flex flex-row gap-1 items-center bg-blue-800 dark:bg-indigo-400 shadow-md border p-1 rounded-md`}
                                >
                                    <p className = {`text-white`}>Export Report</p>
                                </button>
                            }
                        </div>
                        <div className={`flex flex-col gap-2 mt-2`}>
                            <p className={`font-bold text-2xl`}>EPG Stats</p>
                            {
                               epgCoverage.isError ?
                                   <div className={`flex items-center`}>
                                       <p className={`font-bold`}>Error loading EPG Stats. Please try again later.</p>
                                   </div>
                                : epgCoverage.isSuccess && epgCoverage.data && (
                                    <div className={`grid grid-cols-3 gap-4`}>
                                        <div className={`p-5 shadow-md bg-white dark:!bg-brand-700 rounded-lg`}>
                                            <div className={``}>
                                                <p className={`text-md font-bold`}>Distinct Events</p>
                                                <p className={`text-2xl font-bold text-gray-500`}>{`${new Intl.NumberFormat("en-US").format(epgCoverage.data.summary.uniqueEpgEventCount)}`}</p>
                                            </div>
                                        </div>
                                        <div className={`p-5 shadow-md bg-white dark:!bg-brand-700 rounded-lg`}>
                                            <div className={``}>
                                                <p className={`text-md font-bold`}>Event Airings</p>
                                                <div className={`flex gap-2 items-end`}>
                                                    <p className={`text-2xl font-bold text-gray-500`}>
                                                        {new Intl.NumberFormat("en-US").format(epgCoverage.data
                                                            .summary
                                                            .totalEpgEventAiringsCount)}
                                                    </p>
                                                    <p className={`text-md font-bold text-gray-300`}>
                                                        {`(Per channel average: ${new Intl.NumberFormat("en-US").format(epgCoverage
                                                            .data
                                                            .summary
                                                            .totalEpgEventAiringsCount / 10)})`}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`p-5 shadow-md bg-white dark:!bg-brand-700 rounded-lg`}>
                                            <div className={``}>
                                                <p className={`text-md font-bold`}>Images</p>
                                                <p className={`text-2xl font-bold text-gray-500`}>{new Intl.NumberFormat("en-US").format(epgCoverage.data.summary.imagesEpgEventCount)}</p>
                                            </div>
                                        </div>
                                    </div>
                               )
                            }
                        </div>
                        {vodContentReport.isFetched &&
                            <div className = {`flex flex-col gap-2 mt-4`}>
                                <p className = {`font-bold text-2xl`}>VOD Stats</p>
                                {
                                    vodContentReport.isError ? (
                                        <div className = {`flex items-center`}>
                                            <p className = {`font-bold`}>Error loading vod stats. Please try again later.</p>
                                        </div>
                                    ) : vodContentReport.isSuccess && vodContentReport.data && (
                                        <div className = {`grid grid-cols-2 gap-4`}>
                                            <ContentStats contentReport = {vodContentReport.data} />
                                            <ImageStats vodImageStats = {{
                                                imagesTotal: vodContentReport.data.imagesTotal,
                                                values: vodContentReport.data.contentImagesReport,
                                            }}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                        }
                        {
                            epgCoverage.isError ? (
                                    <div className={`flex flex-row items-center`}>
                                        <p className={`font-bold`}>Error loading the coverage report. Please try again later.</p>
                                    </div>
                                )
                                : epgCoverage.isSuccess && epgCoverage.data && <CoverageReport channelCoverages = {epgCoverage.data.channelCoverages} />
                        }
                    </div>
                </div>
            </AdminLayout>
        </>
);
};

export default AdminHomeView;